<template>
  <v-card>
    <v-app-bar flat dense>
      <v-toolbar-title
        ><span class="title">
          Withdrawals
        </span></v-toolbar-title
      >
      <v-spacer></v-spacer>
      <v-btn outlined small color="indigo" @click="showDialog">
        {{ $t("button.newRequest") }}
      </v-btn>
      <v-btn icon @click="getData">
        <v-icon color="indigo">refresh</v-icon>
      </v-btn>
    </v-app-bar>
    <v-divider></v-divider>
    <v-card-text class="pa-0">
      <v-data-table
        :headers="headers"
        :items="listWithdrawals"
        item-key="id"
        :options.sync="pagination"
        :server-items-length="totalWithdrawals"
        :loading="loading"
        :footer-props="footerOptions"
      >
        <template v-slot:item.received_wallet="{ item }">
          <a
            :href="verifyAddress(item.received_wallet, item.currency)"
            target="_blank"
            >{{ formatEllipsisAddress(item.received_wallet) }}</a
          >
        </template>

        <template v-slot:item.received_amount="{ item }">
          {{ showCryptoAmount(item.received_amount, item.currency) }}
        </template>

        <template v-slot:item.fee="{ item }">
          {{ showCryptoAmountFee(item.fee, item.network) }}
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip label outlined small :color="getColorByStatus(item.status)"
            >{{ item.status | uppercase }}
          </v-chip>
        </template>

        <template v-slot:item.created_at="{ item }">
          {{ item.created_at | moment("YYYY-MM-DD HH:mm:ss") }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            outlined
            small
            color="indigo"
            @click="resendConfirm(item)"
            v-if="item.status === 'unconfirmed'"
            class="mr-1"
          >
            {{ $t("menu.payoutSettings.resendConfirmation") }}
          </v-btn>

          <v-btn
            outlined
            small
            color="error"
            @click="cancelWithdrawal(item)"
            v-if="canCancel(item)"
          >
            {{ $t("button.cancel") }}
          </v-btn>
        </template>
      </v-data-table>
      <v-divider></v-divider>
    </v-card-text>

    <v-dialog v-model="dialog" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("dialog.withdrawal.title") }}</span>
        </v-card-title>
        <v-card-text>
          <div class="subheading" v-if="withdrawal.currency">
            Available {{ amountAvailable }}
            {{ withdrawal.currency }} (Subtracted {{ amountBuffer }}
            {{ withdrawal.currency }} buffer for network fee)
          </div>
          <v-layout wrap>
            <v-flex xs12>
              <v-form name="formApp" ref="formApp" :model="withdrawal">
                <v-select
                  v-model="withdrawal.currency"
                  :items="listCryptoCurrencies"
                  :label="$t('dialog.withdrawal.selectCoin') + ' *'"
                  @change="getWithdrawnOptions"
                  clearable
                ></v-select>

                <v-select
                  v-model="withdrawal.received_wallet"
                  :items="walletsData"
                  :item-text="showWalletTitle"
                  item-value="address"
                  :label="$t('dialog.withdrawal.selectWallet') + ' *'"
                  :disabled="!withdrawal.currency"
                  clearable
                ></v-select>

                <v-text-field
                  :label="$t('menu.payoutSettings.walletAddress') + ' *'"
                  v-model="withdrawal.received_wallet"
                  disabled
                  required
                ></v-text-field>

                <v-text-field
                  :label="'Amount (' + withdrawal.currency + ') *'"
                  v-model="withdrawal.amount"
                  type="number"
                  required
                  :hint="getHintDigit"
                  persistent-hint
                ></v-text-field>
              </v-form>
            </v-flex>
          </v-layout>
          <small>{{ $t("common.noticeRequiredField") }}</small>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="cancel"
            >{{ $t("button.cancel") }}
          </v-btn>
          <v-btn color="blue darken-1" text @click="submitApp"
            >{{ $t("button.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      :timeout="3000"
      bottom
      right
      :color="snackbar.color"
      v-model="snackbar.show"
    >
      {{ snackbar.text }}
      <v-btn dark text @click.native="snackbar.show = false" icon>
        <v-icon>close</v-icon>
      </v-btn>
    </v-snackbar>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import {
  verifyAddress,
  showCryptoAmount,
  showCryptoAmountFee,
  cryptoPrecision,
  formatEllipsisAddress
} from "@/util/cryptoCurrencies";
import Util from "@/util";

export default {
  name: "TableApp",
  data(self) {
    return {
      dialog: false,
      loading: false,
      pagination: {
        page: 1,
        itemsPerPage: 10
      },
      footerOptions: {
        showFirstLastPage: true,
        showCurrentPage: true,
        itemsPerPageOptions: [5, 10, 15, 20, 25, 30]
      },
      headers: [
        {
          text: self.$t("table.withdrawal.receivedWallet"),
          value: "received_wallet",
          sortable: false,
          width: "250"
        },
        {
          text: self.$t("table.withdrawal.requestAmount"),
          value: "amount",
          align: "end",
          sortable: false,
          width: "150"
        },
        { text: "", value: "currency", sortable: false, width: "70" },
        {
          text: self.$t("table.withdrawal.receivedAmount"),
          value: "received_amount",
          align: "end",
          sortable: false,
          width: "150"
        },
        {
          text: self.$t("table.withdrawal.fee"),
          value: "fee",
          align: "end",
          sortable: false,
          width: "150"
        },
        {
          text: self.$t("common.status"),
          value: "status",
          sortable: false,
          width: "150"
        },
        {
          text: self.$t("common.createdAt"),
          value: "created_at",
          sortable: false,
          width: "200"
        },
        { text: "", value: "actions", sortable: false, width: "350" }
      ],
      editedIndex: -1,
      withdrawal: {
        received_wallet: "",
        amount: 0,
        currency: ""
      },
      defaultItem: {
        received_wallet: "",
        amount: 0,
        currency: ""
      },
      snackbar: {
        show: false,
        text: "",
        color: ""
      },
      amountAvailable: 0,
      amountBuffer: 0
    };
  },
  computed: {
    ...mapGetters([
      "listCryptoCurrencies",
      "listWithdrawals",
      "totalWithdrawals"
    ]),
    walletsData() {
      if (this.withdrawal.currency) {
        return this.$store.getters.getWalletsByCoin(this.withdrawal.currency);
      } else {
        return [];
      }
    },
    getHintDigit() {
      let count = cryptoPrecision(this.withdrawal.currency);
      return this.$t("dialog.withdrawal.hintDigits", { count: count });
    }
  },
  methods: {
    getColorByStatus(status) {
      return Util.getColorByStatus(status);
    },
    getData() {
      this.loading = true;
      this.$store
        .dispatch("fetchListWithdrawals", {
          page: this.pagination.page,
          per_page: this.pagination.itemsPerPage
        })
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    submitApp() {
      if (this.editedIndex > -1) {
        this.$store
          .dispatch("updateWithdrawal", this.withdrawal)
          .then(() => {
            this.$store.dispatch("fetchListWithdrawals", {
              page: this.pagination.page,
              per_page: this.pagination.itemsPerPage
            });
            this.snackbar = {
              show: true,
              color: "green",
              text: "Successfully."
            };
            this.close();
          })
          .catch(error => {
            this.snackbar = {
              show: true,
              color: "red",
              text: error.response.data.message
            };
          });
      } else {
        this.$store
          .dispatch("postNewWithdrawal", this.withdrawal)
          .then(() => {
            this.$store.dispatch("fetchListWithdrawals", {
              page: this.pagination.page,
              per_page: this.pagination.itemsPerPage
            });
            this.snackbar = {
              show: true,
              color: "green",
              text: "Successfully."
            };
            this.close();
          })
          .catch(error => {
            this.snackbar = {
              show: true,
              color: "red",
              text: error.response.data.message
            };
          });
      }
    },
    close() {
      this.dialog = false;
      setTimeout(() => {
        this.withdrawal = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      }, 300);
    },
    editItem(item) {
      this.editedIndex = this.listWithdrawals.indexOf(item);
      this.withdrawal = Object.assign({}, item);
      this.dialog = true;
    },
    cancel() {
      this.close();
      // this.$refs.formApp.reset();
    },
    showDialog() {
      this.dialog = true;
      this.$store.dispatch("fetchListCryptoCurrency");
    },
    getWallets(coin) {
      if (coin) {
        this.withdrawal.received_wallet = "";
        this.withdrawal.amount = 0;
        this.$store
          .dispatch("fetchListPayoutWallet", {
            query: {
              page: 1,
              per_page: 99,
              status: "confirmed"
            },
            coin: coin
          })
          .then(() => {})
          .catch(() => {});
      }
    },
    showWalletTitle(item) {
      if (item.title) {
        return item.title + " (" + item.address + ")";
      } else {
        return item.address;
      }
    },
    resendConfirm(item) {
      this.$store
        .dispatch("resendConfirmationWithdrawal", item.id)
        .then(() => {
          this.$store.dispatch("fetchListWithdrawals", {
            page: this.pagination.page,
            per_page: this.pagination.itemsPerPage
          });
          this.snackbar = {
            show: true,
            color: "green",
            text: "Successfully."
          };
        })
        .catch(error => {
          this.snackbar = {
            show: true,
            color: "red",
            text: error.response.data.error
          };
        });
    },
    canCancel(item) {
      return item.status === "unconfirmed" || item.status === "confirmed";
    },
    cancelWithdrawal(item) {
      this.$store
        .dispatch("cancelConfirmationWithdrawal", item.id)
        .then(() => {
          this.$store.dispatch("fetchListWithdrawals", {
            page: this.pagination.page,
            per_page: this.pagination.itemsPerPage
          });
          this.snackbar = {
            show: true,
            color: "green",
            text: "Successfully."
          };
        })
        .catch(error => {
          this.snackbar = {
            show: true,
            color: "red",
            text: error.response.data.error
          };
        });
    },
    getAmountAvailable(coin) {
      this.$store
        .dispatch("getAmountAvailable", coin)
        .then(response => {
          this.amountAvailable = response["total"];
          this.amountBuffer = response["buffer"];
        })
        .catch(() => {});
    },
    getWithdrawnOptions(coin) {
      if (coin) {
        this.getAmountAvailable(coin);
        this.getWallets(coin);
      } else {
        this.amountAvailable = 0;
        this.amountBuffer = 0;
        this.withdrawal = Object.assign({}, this.defaultItem);
      }
    },
    formatEllipsisAddress,
    verifyAddress,
    showCryptoAmount,
    showCryptoAmountFee
  },
  mounted() {
    this.getData();
  },
  watch: {
    pagination: {
      handler() {
        this.getData();
      },
      deep: true
    }
  }
};
</script>
<style lang="stylus">
.currency-amount {
    padding-right: 2px !important;
}

.currency-code {
    padding-left: 2px !important;
}
</style>
